import { memo, useCallback, useMemo, useState } from 'react';
import {
    Input,
    InputNumber,
    Typography,
    DatePicker,
    Checkbox,
    TimePicker,
    Select,
    Popover,
    Space,
    Button,
    theme,
} from 'antd';
import { LinkOutlined } from '@ant-design/icons';
import { Timestamp } from 'firebase/firestore';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';

import {
    UIField,
    EntityRecord,
    ValidationStatus,
    FieldType,
} from '#types/System.types';
import { MAX_LENGTHS, MIN_LENGTHS, MAX_LIMITED_WIDTH_VIEWPORT } from '#types/System.Parameters.types';
import { QosComponent } from '../../orgs/clw/components/ClwQos';
import GraphUI from '../rendering/fields/GraphUI';
import CommunicationLogUI from './fields/CommunicationLog';
import FormBannerUI from './fields/FormBannerUI';
import LongTextUI from './fields/LongTextUI';
import AddressUI from './fields/AddressUI';
import TelephoneUI from './fields/TelephoneUI';
import RelationshipTree from './fields/RelationshipTree';
import SellPriceUI from './fields/SellPriceUI';
import UserAndGroupUI from './fields/UserAndGroupUI';
import { ShippingTrackerCompactUI, ShippingTrackerExpandedUI } from '../rendering/fields/ShippingTrackerUI';
import { CompositeField } from '../CompositeField';
import { usePdfExportContext } from '../pdfExport/context/PdfExportContext';
import { useKeybindings } from '@/keyboard';
import { dateOnlyDayjs, dateOnlyTimestamp } from '@/dates';

/**
 * Represents the time range structure with start and end timestamps
 */
type TimeRange = {
    start: Timestamp;
    end: Timestamp;
};

/**
 * Props for the ControlUI component
 */
interface ControlUIProps {
    /** UI configuration for the field */
    uiField: UIField;
    /** Callback when field value changes */
    onChange: (value: any, entityRecord?: EntityRecord | null) => void;
    /** Optional field ID */
    id?: string;
    /** Whether the field is read-only */
    readOnly?: boolean;
    /** Callback when field gains focus */
    onFocus?: () => void;
    /** Callback when field loses focus */
    onBlur?: () => void;
    /** Validation status of the field */
    status?: ValidationStatus;
    /** Current value of the field */
    value: any;
    /** Optional field type definitions (defaults to fieldDataTypes) */
    fieldType?: FieldType;
    /** Compact mode for table cells - shows single line with popup for long text */
    tableMode?: boolean;
    /** When true, value is formula-derived (for sellPriceUI: price/basePrice disabled, ratePercent/rateSign editable) */
    isCalculated?: boolean;
    /** Additional props passed to the control */
    [key: string]: any;
}

/**
 * A dynamic form control component that renders different input types based on field configuration.
 * 
 * **Purpose:**
 * Pure field type multiplexer that maps field data types to appropriate Ant Design controls.
 * Does NOT handle entity references - use EntityRecordWrapper or EntityUIContext for those.
 * 
 * **Supported Field Types:**
 * - Text: shortText, longText, email, phone, password, url
 * - Numbers: number, quantity, currency, percentage
 * - Dates/Times: date, dateTime, time, dateRange, timeRange, dateTimeRange
 * - Selections: singleSelect, multiSelect, yesNo (checkbox)
 * - Complex: table, list, graph, emailList, formBanner
 * - Custom: qos (organization-specific)
 * 
 * @note Entity references should be handled by EntityRecordWrapper (tables/graphs) 
 * or EntityUIContext (forms/entities), NOT by this component.
 */
const ControlUI = memo(({
    id,
    uiField,
    onChange,
    readOnly = false,
    onFocus,
    onBlur,
    status,
    value,
    fieldType,
    enableUndefinedFieldTypes = false,
    tableMode = false,
    isCalculated = false,
    ...props
}: ControlUIProps) => {
    const { token } = theme.useToken();
    const { isPdfExportMode } = usePdfExportContext();
    const ft = fieldType;
    const effectiveType = ft?.fieldDataTypeId;
    const fieldId = uiField.fieldId;
    const defaultPanelAnchor = useMemo(() => dayjs(), []);
    const defaultRangePanelAnchor = useMemo(() => {
        const start = dayjs();
        return [start, start.add(1, 'month')] as [Dayjs, Dayjs];
    }, []);

    const expectsTimestampValue =
        effectiveType === 'date' ||
        effectiveType === 'dateTime' ||
        effectiveType === 'time';

    const [panelValue, setPanelValue] = useState<Dayjs | null>(null);
    const [rangePanelValue, setRangePanelValue] = useState<[Dayjs, Dayjs] | null>(null);
    const [longTextPopoverOpen, setLongTextPopoverOpen] = useState(false);
    const [popupOpen, setPopupOpen] = useState(false);
    const { onKeyDown: openPopupKeyDown } = useKeybindings(
        popupOpen ? {} : { 'field.openPopup': () => setPopupOpen(true) }
    );
    const expandableKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (popupOpen) {
            e.stopPropagation();
            return;
        }
        openPopupKeyDown(e);
    }, [popupOpen, openPopupKeyDown]);

    const expectsTimeRangeValue =
        effectiveType === 'timeRange' ||
        effectiveType === 'dateRange' ||
        effectiveType === 'dateTimeRange';

    const timestampMillis = useMemo(() => {
        if (!expectsTimestampValue) return null;
        if (value instanceof Timestamp) return value.toMillis();
        if (typeof value === 'object' && value !== null) {
            const v: any = value;

            if (typeof v.toMillis === 'function') {
                const ms = v.toMillis();
                return typeof ms === 'number' && Number.isFinite(ms) ? ms : null;
            }

            if (typeof v.toDate === 'function') {
                const d = v.toDate();
                if (d instanceof Date) {
                    const ms = d.getTime();
                    return Number.isFinite(ms) ? ms : null;
                }
            }

            const seconds =
                typeof v.seconds === 'number'
                    ? v.seconds
                    : typeof v._seconds === 'number'
                        ? v._seconds
                        : null;
            const nanos =
                typeof v.nanoseconds === 'number'
                    ? v.nanoseconds
                    : typeof v._nanoseconds === 'number'
                        ? v._nanoseconds
                        : 0;

            if (seconds !== null) {
                const ms = seconds * 1000 + nanos / 1000000;
                return Number.isFinite(ms) ? ms : null;
            }
        }
        return null;
    }, [expectsTimestampValue, value]);

    /**
     * `date` and `dateRange` hold a calendar day anchored at start of day in
     * `defaultTimeZone`, so they read back through `dateOnlyDayjs` and write through
     * `dateOnlyTimestamp` (SYD-2507). `dateTime`, `time`, `timeRange` and
     * `dateTimeRange` are true instants and deliberately keep the browser-local
     * `dayjs(millis)` / `Timestamp.fromDate` conversion — the wall clock the user
     * picked is the value, and shifting it to Chicago would change the instant.
     */
    const dayjsValue = useMemo(() => {
        if (timestampMillis === null) return null;
        return effectiveType === 'date'
            ? dateOnlyDayjs(timestampMillis)
            : dayjs(timestampMillis);
    }, [effectiveType, timestampMillis]);

    const computedPickerValueForSingle = useMemo(() => {
        if (!(effectiveType === 'date' || effectiveType === 'dateTime')) return undefined;
        return panelValue ?? dayjsValue ?? defaultPanelAnchor;
    }, [dayjsValue, defaultPanelAnchor, effectiveType, panelValue]);

    const [rangeStartMillis, rangeEndMillis] = useMemo(() => {
        if (!expectsTimeRangeValue) return [null, null] as const;
        const range = value as TimeRange;

        const startVal: any = range?.start;
        const endVal: any = range?.end;

        const startMillis = (() => {
            if (!startVal) return null;
            if (startVal instanceof Timestamp) return startVal.toMillis();
            if (typeof startVal?.toMillis === 'function') {
                const ms = startVal.toMillis();
                return typeof ms === 'number' && Number.isFinite(ms) ? ms : null;
            }
            if (typeof startVal?.toDate === 'function') {
                const d = startVal.toDate();
                if (d instanceof Date) {
                    const ms = d.getTime();
                    return Number.isFinite(ms) ? ms : null;
                }
            }
            const seconds =
                typeof startVal?.seconds === 'number'
                    ? startVal.seconds
                    : typeof startVal?._seconds === 'number'
                        ? startVal._seconds
                        : null;
            const nanos =
                typeof startVal?.nanoseconds === 'number'
                    ? startVal.nanoseconds
                    : typeof startVal?._nanoseconds === 'number'
                        ? startVal._nanoseconds
                        : 0;
            if (seconds !== null) {
                const ms = seconds * 1000 + nanos / 1000000;
                return Number.isFinite(ms) ? ms : null;
            }
            return null;
        })();

        const endMillis = (() => {
            if (!endVal) return null;
            if (endVal instanceof Timestamp) return endVal.toMillis();
            if (typeof endVal?.toMillis === 'function') {
                const ms = endVal.toMillis();
                return typeof ms === 'number' && Number.isFinite(ms) ? ms : null;
            }
            if (typeof endVal?.toDate === 'function') {
                const d = endVal.toDate();
                if (d instanceof Date) {
                    const ms = d.getTime();
                    return Number.isFinite(ms) ? ms : null;
                }
            }
            const seconds =
                typeof endVal?.seconds === 'number'
                    ? endVal.seconds
                    : typeof endVal?._seconds === 'number'
                        ? endVal._seconds
                        : null;
            const nanos =
                typeof endVal?.nanoseconds === 'number'
                    ? endVal.nanoseconds
                    : typeof endVal?._nanoseconds === 'number'
                        ? endVal._nanoseconds
                        : 0;
            if (seconds !== null) {
                const ms = seconds * 1000 + nanos / 1000000;
                return Number.isFinite(ms) ? ms : null;
            }
            return null;
        })();

        if (startMillis !== null && endMillis !== null) {
            return [startMillis, endMillis] as const;
        }
        return [null, null] as const;
    }, [expectsTimeRangeValue, value]);

    const rangeDayjsValue = useMemo(() => {
        if (rangeStartMillis === null || rangeEndMillis === null) return null;
        const toDayjs = (millis: number) =>
            effectiveType === 'dateRange' ? dateOnlyDayjs(millis) : dayjs(millis);
        return [toDayjs(rangeStartMillis), toDayjs(rangeEndMillis)] as [Dayjs, Dayjs];
    }, [effectiveType, rangeEndMillis, rangeStartMillis]);

    const computedPickerValueForRange = useMemo(() => {
        if (!expectsTimeRangeValue) return undefined;
        return rangePanelValue ?? rangeDayjsValue ?? defaultRangePanelAnchor;
    }, [defaultRangePanelAnchor, expectsTimeRangeValue, rangeDayjsValue, rangePanelValue]);

    const commonProps = {
        id: id || fieldId || '',
        onFocus,
        onBlur,
        status,
        variant: 'filled' as const,
        disabled: readOnly || isCalculated,
        ...uiField.props,
        ...props,
        style: {
            width: '100%',
            maxWidth: MAX_LIMITED_WIDTH_VIEWPORT,
            ...(uiField.props?.style ?? {}),
            ...(props.style ?? {}),
        },
    };

    const renderControl = useCallback(() => {
        if (effectiveType === 'composite') {
            return (
                <CompositeField
                    compositeFieldTypeId={fieldId}
                    fieldId={fieldId}
                    value={value}                    onChange={onChange}
                    disabled={readOnly}
                    isFormulaColumn={isCalculated}
                    tableMode={tableMode}
                    title={props.title}
                />
            );
        }

        switch (effectiveType) {
            case 'shortText':
            case 'text':
            case 'email':
            case 'password':
                return <Input
                    {...commonProps}
                    value={value}
                    maxLength={MAX_LENGTHS.shortText}
                    minLength={MIN_LENGTHS.shortText}
                    autoComplete="off"
                    onChange={(e) => onChange(e.target.value)}
                    style={{
                        overflow: 'hidden',
                        ...commonProps.style,
                    }}
                />;

            case 'phone':
                return (
                    <TelephoneUI
                        {...commonProps}
                        value={value}
                        onChange={onChange}
                    />
                );

            case 'sellPriceUI':
                return (
                    <SellPriceUI
                        {...commonProps}
                        disabled={readOnly}
                        value={value}
                        onChange={onChange}
                        isFormulaColumn={isCalculated}
                        defaultFormulaTypeId={ft?.props?.formulaTypeId}
                    />
                );

            case 'userAndGroup':
                return (
                    <UserAndGroupUI
                        {...commonProps}
                        disabled={isCalculated}
                        readOnly={readOnly}
                        value={value}
                        onChange={onChange}
                        selectionMode={ft?.props?.selectionMode}
                        multiSelect={ft?.props?.multiSelect}
                        restrictToBusinessGroupIds={ft?.props?.restrictToBusinessGroupIds}
                        enableWeights={ft?.props?.enableWeights}
                        maxSelections={ft?.props?.maxSelections}
                        enforceWeightTotal={ft?.props?.enforceWeightTotal}
                        targetWeightTotal={ft?.props?.targetWeightTotal}
                    />
                );

            case 'shippingTrackerCompact':
                return (
                    <ShippingTrackerCompactUI
                        fieldId={fieldId}
                        value={value}
                        onChange={onChange}
                        disabled={readOnly || isCalculated}
                        status={status}
                        style={commonProps.style}
                    />
                );

            case 'shippingTrackerExpanded':
                return (
                    <ShippingTrackerExpandedUI
                        fieldId={fieldId}
                        value={value}
                        onChange={onChange}
                        disabled={readOnly || isCalculated}
                        status={status}
                        style={commonProps.style}
                    />
                );

            case 'longText':
            case 'richText':
            case 'json':
            case 'xml':
            case 'html':
            case 'ipAddress':
                if (tableMode) {
                    return (
                        <Popover
                            trigger="click"
                            open={longTextPopoverOpen}
                            onOpenChange={setLongTextPopoverOpen}
                            styles={{ container: { padding: 2 } }}
                            content={
                                <Input.TextArea
                                    value={value}
                                    maxLength={MAX_LENGTHS.longText}
                                    minLength={MIN_LENGTHS.longText}
                                    autoComplete="off"
                                    onChange={(e) => onChange(e.target.value)}
                                    autoSize={{ minRows: 3, maxRows: 10 }}
                                    style={{ width: 300, maxWidth: `calc(100vw - ${token.marginLG * 2}px)`, padding: '0 4px' }}
                                />
                            }
                        >
                            <Input
                                {...commonProps}
                                value={value}
                                readOnly
                                autoComplete="off"
                                style={{ cursor: 'pointer', ...uiField.props?.style }}
                            />
                        </Popover>
                    );
                }
                return <LongTextUI
                    {...commonProps}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                />;

            case 'address':
                return (
                    <AddressUI
                        {...commonProps}
                        value={value}
                        onChange={onChange}
                        showRecipient={ft?.props?.showRecipient}
                        showContact={ft?.props?.showContact}
                        recordSources={ft?.props?.recordSources}
                    />
                );

            case 'url':
                return (
                    <Space.Compact style={{ width: '100%' }}>
                        <Input
                            {...commonProps}
                            value={value}
                            type='url'
                            autoComplete="off"
                            onChange={(e) => onChange(e.target.value)}
                        />
                        <Button
                            type="text"
                            icon={<LinkOutlined />}
                            onClick={() => {
                                if (value) {
                                    window.open(ft?.parser?.(value), '_blank', 'noopener,noreferrer');
                                }
                            }}
                        />
                    </Space.Compact>
                );

            case 'quantity':
            case 'number':
            case 'currency':
            case 'percentage':
                return (
                    <InputNumber
                        {...commonProps}
                        value={value}
                        formatter={isPdfExportMode
                            ? (val) => {
                                const prefix = ft?.prefix ? `${ft.prefix} ` : '';
                                const suffix = ft?.suffix ? ` ${ft.suffix}` : '';
                                const formatted = ft?.formatter ? ft.formatter(val) : val;
                                return `${prefix}${formatted ?? ''}${suffix}`;
                            }
                            : ft?.formatter}
                        parser={ft?.parser}
                        autoComplete="off"
                        styles={{
                            input: {
                                whiteSpace: 'nowrap',
                                fontVariantNumeric: 'tabular-nums',
                            }
                        }}
                        prefix={!isPdfExportMode && ft?.prefix && (
                            <Typography.Text type="secondary">
                                {ft?.prefix}
                            </Typography.Text>
                        )}
                        suffix={!isPdfExportMode && ft?.suffix && (
                            <Typography.Text type="secondary">
                                {ft?.suffix}
                            </Typography.Text>
                        )}
                        onChange={(value) => onChange(value)}
                    />
                );

            case 'yesNo':
                return (
                    <Checkbox
                        {...commonProps}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexGrow: 1,
                            ...(uiField.props?.style ?? {}),
                        }}
                        checked={Boolean(value)}
                        onChange={(e) => onChange(e.target.checked)}
                    />
                );

            case 'singleSelect':
                return (
                    <Select
                        {...commonProps}
                        open={popupOpen}
                        onOpenChange={setPopupOpen}
                        onKeyDown={expandableKeyDown}
                        allowClear
                        value={value ?? undefined}
                        onChange={(selectedValue) => onChange(selectedValue ?? null)}
                    />
                );

            case 'multiSelect':
                return (
                    <Select
                        maxTagCount={3}
                        maxTagTextLength={8}
                        {...commonProps}
                        open={popupOpen}
                        onOpenChange={setPopupOpen}
                        onKeyDown={expandableKeyDown}
                        mode="multiple"
                        allowClear
                        value={value as (string | number)[]}
                        onChange={(selectedValues) => onChange(selectedValues ?? [])}
                    />
                );

            case 'date':
                return (
                    <DatePicker
                        {...commonProps}
                        open={popupOpen}
                        onKeyDown={expandableKeyDown}
                        value={dayjsValue}
                        autoComplete="off"
                        pickerValue={computedPickerValueForSingle}
                        onOpenChange={(open) => {
                            setPopupOpen(open);
                            if (open) {
                                if (expectsTimestampValue && value && !(value instanceof Timestamp)) {
                                    console.error(`ControlUI: Expected Timestamp for date field '${fieldId}', got:`, value);
                                }
                                setPanelValue(dayjsValue ?? dayjs());
                            }
                        }}
                        onPanelChange={(nextPanelValue) => setPanelValue(nextPanelValue)}
                        onChange={(date) => {
                            if (!date) {
                                onChange(null);
                                return;
                            }

                            if (!date.isValid()) {
                                return;
                            }

                            setPanelValue(date);
                            onChange(dateOnlyTimestamp(date));
                        }}
                    />
                );

            case 'dateTime':
                return (
                    <DatePicker
                        {...commonProps}
                        open={popupOpen}
                        onKeyDown={expandableKeyDown}
                        showTime
                        value={dayjsValue}
                        autoComplete="off"
                        pickerValue={computedPickerValueForSingle}
                        onOpenChange={(open) => {
                            setPopupOpen(open);
                            if (open) {
                                if (expectsTimestampValue && value && !(value instanceof Timestamp)) {
                                    console.error(`ControlUI: Expected Timestamp for dateTime field '${fieldId}', got:`, value);
                                }
                                setPanelValue(dayjsValue ?? dayjs());
                            }
                        }}
                        onPanelChange={(nextPanelValue) => setPanelValue(nextPanelValue)}
                        onChange={(date) => {
                            if (!date) {
                                onChange(null);
                                return;
                            }

                            if (!date.isValid()) {
                                return;
                            }

                            setPanelValue(date);
                            onChange(Timestamp.fromDate(date.toDate()));
                        }}
                    />
                );

            case 'time':
                return (
                    <TimePicker
                        {...commonProps}
                        open={popupOpen}
                        onOpenChange={setPopupOpen}
                        onKeyDown={expandableKeyDown}
                        value={dayjsValue}
                        autoComplete="off"
                        onChange={(time) => {
                            if (!time) {
                                onChange(null);
                                return;
                            }
                            if (!time.isValid()) return;
                            onChange(Timestamp.fromDate(time.toDate()));
                        }}
                    />
                );

            case 'timeRange':
            case 'dateRange':
            case 'dateTimeRange': {
                const rangeTimestamp =
                    effectiveType === 'dateRange'
                        ? dateOnlyTimestamp
                        : (picked: Dayjs) => Timestamp.fromDate(picked.toDate());
                return (
                    <DatePicker.RangePicker
                        {...commonProps}
                        open={popupOpen}
                        onKeyDown={expandableKeyDown}
                        allowEmpty={true}
                        showTime={effectiveType === 'timeRange' || effectiveType === 'dateTimeRange'}
                        value={rangeDayjsValue}
                        autoComplete="off"
                        pickerValue={computedPickerValueForRange}
                        onOpenChange={(open) => {
                            setPopupOpen(open);
                            if (open) {
                                if (expectsTimeRangeValue && value && rangeDayjsValue === null) {
                                    console.error(`ControlUI: Expected TimeRange with Timestamp objects for ${effectiveType} field '${fieldId}', got:`, value);
                                }

                                const start = rangeDayjsValue?.[0] ?? dayjs();
                                setRangePanelValue([start, rangeDayjsValue?.[1] ?? start.add(1, 'month')]);
                            }
                        }}
                        onPanelChange={(nextPanelValues) => setRangePanelValue(nextPanelValues as [Dayjs, Dayjs])}
                        onChange={(dates) => {
                            if (
                                dates &&
                                dates[0] &&
                                dates[1] &&
                                dates[0].isValid() &&
                                dates[1].isValid()
                            ) {
                                setRangePanelValue([dates[0], dates[1]]);
                                onChange({
                                    start: rangeTimestamp(dates[0]),
                                    end: rangeTimestamp(dates[1])
                                });
                            } else {
                                onChange(null);
                            }
                        }}
                    />
                );
            }

            case 'relationshipTree':
                return (
                    <RelationshipTree
                        {...commonProps}
                        value={value}
                        onChange={onChange}
                        config={ft?.props?.config}
                    />
                );

            case 'graph':
                return (
                    <GraphUI
                        {...commonProps}
                        value={value || []}
                        onChange={onChange}
                    />
                );

            case 'qos':
                return (
                    <QosComponent
                        {...commonProps}
                        displayOnly={tableMode}
                        qos={value}
                        onChange={onChange}
                    />
                );

            case 'emailList':
            case 'communicationLog':
                return (
                    <CommunicationLogUI
                        {...commonProps}
                        value={value}
                        columns={fieldType?.columns}
                        onChange={onChange}
                        disabled={readOnly}
                    />
                );

            case 'formBanner':
                return (
                    <FormBannerUI
                        {...commonProps}
                        value={value || 'none'}
                        onChange={onChange}
                        disabled={readOnly}
                    />
                );

            default:
                console.error(`Unknown field type when rendering '${fieldId}'`);
                return (
                    <Popover
                        title="Missing Field Type"
                        content={
                            <div>
                                <Typography.Paragraph>
                                    The field type for <strong>{fieldId}</strong> may have been removed or is no longer supported.<br />
                                    Please contact support for assistance.
                                </Typography.Paragraph>
                            </div>
                        }
                        trigger="hover"
                        placement="top"
                        aria-label="missing-field-type-popup"
                    >
                        <Input
                            disabled
                            placeholder="Missing field type"
                            type='dashed'
                            status='warning'
                            autoComplete="off"
                            style={{ cursor: 'help' }}
                        />
                    </Popover>
                );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        id,
        effectiveType,
        commonProps,
        value,
        onChange,
        fieldId,
        uiField.columns,
        uiField.props,
        readOnly,
        popupOpen,
        openPopupKeyDown,
        token.marginLG
    ]);

    return renderControl();
});

ControlUI.displayName = 'ControlUI';

export default ControlUI;
